import { useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { apiFetch } from '@/lib/apiClient'
import { useEscapeKey } from '@/common/hooks/useEscapeKey'
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick'
import { ExportMyDataButton } from './ExportMyDataButton'

/**
 * BillingIssueModal — PlanChoice context `fix_payment` (PDR 012, Phase 1).
 *
 * Shown when a write to a lapsed (nonpayment) workspace returns
 * `403 WORKSPACE_LOCKED` — surfaced app-wide via the `xavior:workspace-locked`
 * event from the http layer (see BillingIssueModalHost + lib/http.ts).
 *
 *   - OWNER  → "Fix billing" (Stripe customer portal, POST /payment/portal) so
 *              they can resume the past-due/canceled subscription, plus the
 *              read-only "Export my data" affordance.
 *   - MEMBER → read-only explanation ("the owner needs to fix billing") + export.
 *              Members never see the portal CTA (billing is owner-only).
 *
 * Reads/exports are NOT blocked server-side, so this modal is purely additive
 * UI — the offending write still rejects underneath. `context` is pinned to
 * `fix_payment` here; it is the seam for the later PlanChoice generalization
 * (upgrade / add_workspace), which is OUT of scope for Phase 1.
 */

export type BillingIssueContext = 'fix_payment'

export interface BillingIssueModalProps {
  open: boolean
  /** Phase 1: always `fix_payment`. Reserved seam for PlanChoice generalization. */
  context?: BillingIssueContext
  isOwner: boolean
  authToken: string | null
  reason?: 'nonpayment' | 'unresolved_ownership'
  onClose: () => void
}

export function BillingIssueModal({
  open,
  isOwner,
  authToken,
  reason,
  onClose,
}: BillingIssueModalProps) {
  const [openingPortal, setOpeningPortal] = useState(false)
  const [portalError, setPortalError] = useState<string | null>(null)
  const [contentRef, handleOverlayClick, handleOverlayMouseDown] =
    useModalOverlayClick<HTMLDivElement>(onClose)
  useEscapeKey(onClose, open)

  if (!open) return null

  const handleFixBilling = async () => {
    // Stripe's hosted portal must not open inside the Capacitor WKWebView.
    if (Capacitor.isNativePlatform()) {
      setPortalError('Manage billing on the web app at xavior.ai.')
      return
    }
    setOpeningPortal(true)
    setPortalError(null)
    try {
      const res = await apiFetch<{ url?: string }>('payment/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnUrl: window.location.href }),
        authToken: authToken ?? undefined,
      })
      if (res?.url) {
        window.location.href = res.url
      } else {
        setPortalError('Could not open the billing portal. Try Settings → Billing.')
      }
    } catch {
      setPortalError('Could not open the billing portal. Try Settings → Billing.')
    } finally {
      setOpeningPortal(false)
    }
  }

  const body =
    reason === 'unresolved_ownership'
      ? 'This workspace is read-only while its ownership is being resolved.'
      : isOwner
        ? 'This workspace is read-only because its subscription lapsed. Restore access by fixing your billing details.'
        : 'This workspace is read-only until the owner fixes the billing. You can still read and export your data.'

  return (
    <div
      className="billing-issue-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-5"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Billing issue"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={contentRef}
        className="bg-white rounded-2xl p-10 w-full text-center border border-gray-100"
        style={{ maxWidth: '420px', boxShadow: '0 2px 24px rgba(0,0,0,0.06)' }}
      >
        <div className="flex items-center justify-center gap-3 mb-8">
          <div
            className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: '#0ea5e9' }}
          >
            <span className="text-white text-sm font-bold">X</span>
          </div>
          <span
            className="text-lg font-medium text-gray-900"
            style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}
          >
            XAVIOR
          </span>
        </div>

        <div className="text-4xl mb-4">⚠️</div>
        <h2
          className="text-2xl font-normal text-gray-900 mb-2"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          {isOwner ? 'Billing needs attention' : 'This workspace is read-only'}
        </h2>
        <p className="text-sm text-gray-500 mb-8">{body}</p>

        <div className="flex flex-col gap-3 justify-center">
          {isOwner && (
            <button
              type="button"
              onClick={handleFixBilling}
              disabled={openingPortal}
              className="w-full px-4 py-3 text-white font-medium rounded-xl transition-all text-sm disabled:opacity-60"
              style={{ background: '#0ea5e9', boxShadow: '0 1px 3px rgba(14,165,233,0.3)' }}
            >
              {openingPortal ? 'Opening billing…' : 'Fix billing'}
            </button>
          )}
          {portalError && <p className="text-xs text-red-500">{portalError}</p>}
          <ExportMyDataButton authToken={authToken} />
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all text-sm"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  )
}
